import {
	defaultsToToday,
	describeFormFieldError,
	FORM_ERROR_MESSAGES,
	FORM_LAYOUT_CHOICE_TYPES,
	formTodayValue,
	type FormDefinitionModel,
	type FormFieldDefinitionModel,
	type FormFieldErrorCode,
	type FormLayoutColumnCount,
	type FormLayoutTextStyle,
	type FormRuleErrorModel,
	type FormValidationResultModel,
} from '@supply-mover-2-0/shared';

/**
 * PD-16596 — what the form renderer shows, as pure functions so the rules are tested
 * apart from React (docs/claude/forms/01-design.md §3, §12).
 *
 * - A field's own error shows once the visitor has left that field, or after they tried
 *   to submit; nobody is scolded for a field they have not reached yet.
 * - "At least one of" messages, and the summary at the top, show after a submit attempt.
 * - Errors the server returned show until the visitor changes that field.
 */

/** The server's 400 body, as the public endpoint returns it. */
export interface FormServerErrorsModel {
	errors?: Partial<Record<string, FormFieldErrorCode>>;
	ruleErrors?: ReadonlyArray<FormRuleErrorModel>;
	/** Form-level codes (FormErrorCode); unknown ones get a general message. */
	formErrors?: ReadonlyArray<string>;
	message?: string;
}

export interface FormVisibleErrorsModel {
	/** Message per field key. */
	fields: Record<string, string>;
	/** "At least one of" messages, keyed by the last field of the rule (shown under it). */
	rules: Record<string, Array<string>>;
	/** Everything to list at the top, in page order; empty until a submit attempt. */
	summary: Array<{ fieldKey: string | null; message: string }>;
}

/**
 * The starting values: a field's default, yes/no fields off (a switch that shows off must
 * mean false, not "not answered"), consent unchecked. PD-16691: a date or date-and-time
 * question set to "Default to today" starts at `now` in the visitor's own time zone; the
 * visitor can change it, and the page sends the date, never the token.
 */
export function initialFormValues(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
	now: Date = new Date(),
): Record<string, unknown> {
	const values: Record<string, unknown> = {};
	for (const field of fields) {
		const fallback = field.defaultValue;
		if (
			defaultsToToday(field) &&
			(field.fieldType === 'date' || field.fieldType === 'datetime')
		) {
			values[field.fieldKey] = formTodayValue(field.fieldType, now);
			continue;
		}
		switch (field.fieldType) {
			case 'boolean':
				values[field.fieldKey] = fallback === 'true';
				break;
			case 'consent':
				values[field.fieldKey] = false;
				break;
			case 'number':
			case 'currency':
				values[field.fieldKey] =
					fallback != null &&
					fallback.trim() !== '' &&
					Number.isFinite(Number(fallback))
						? Number(fallback)
						: null;
				break;
			case 'multiselect':
				values[field.fieldKey] =
					fallback != null && fallback !== ''
						? fallback.split(',').map((choice) => choice.trim())
						: [];
				break;
			default:
				values[field.fieldKey] =
					fallback != null && fallback !== '' ? fallback : null;
		}
	}
	return values;
}

export function visibleFormErrors(
	definition: FormDefinitionModel,
	validation: FormValidationResultModel,
	shown: { touched: ReadonlySet<string>; attempted: boolean },
	server: FormServerErrorsModel | null,
): FormVisibleErrorsModel {
	const visible: FormVisibleErrorsModel = {
		fields: {},
		rules: {},
		summary: [],
	};

	for (const field of definition.fields) {
		if (field.fieldType === 'hidden') {
			continue;
		}
		const code =
			shown.attempted || shown.touched.has(field.fieldKey)
				? validation.errors[field.fieldKey]
				: undefined;
		const serverCode = server?.errors?.[field.fieldKey];
		const chosen = code ?? serverCode;
		if (chosen != null) {
			visible.fields[field.fieldKey] = describeFormFieldError(field, chosen);
		}
	}

	if (shown.attempted) {
		const ruleErrors = [
			...validation.ruleErrors,
			...(server?.ruleErrors ?? []),
		];
		const seen = new Set<string>();
		for (const rule of ruleErrors) {
			const anchor = rule.fieldKeys[rule.fieldKeys.length - 1];
			if (anchor == null || seen.has(rule.message)) {
				continue;
			}
			seen.add(rule.message);
			(visible.rules[anchor] ??= []).push(rule.message);
		}

		// Listed in the order the page shows the questions, so the summary reads top to bottom.
		for (const field of fieldsInPageOrder(definition)) {
			const message = visible.fields[field.fieldKey];
			if (message != null) {
				visible.summary.push({ fieldKey: field.fieldKey, message });
			}
			for (const ruleMessage of visible.rules[field.fieldKey] ?? []) {
				visible.summary.push({
					fieldKey: field.fieldKey,
					message: ruleMessage,
				});
			}
		}
		for (const code of server?.formErrors ?? []) {
			visible.summary.push({ fieldKey: null, message: formErrorMessage(code) });
		}
	}
	return visible;
}

export interface ResolvedFormFieldCellModel {
	kind: 'field';
	field: FormFieldDefinitionModel;
	span: FormLayoutColumnCount;
	/** Choice questions listed in this many columns; null keeps the default input. */
	optionColumns: FormLayoutColumnCount | null;
}

export interface ResolvedFormTextCellModel {
	kind: 'text';
	id: string;
	style: FormLayoutTextStyle;
	text: string;
	span: FormLayoutColumnCount;
}

export type ResolvedFormCellModel =
	| ResolvedFormFieldCellModel
	| ResolvedFormTextCellModel;

export interface ResolvedFormRowModel {
	id: string;
	columns: FormLayoutColumnCount;
	cells: Array<ResolvedFormCellModel>;
}

export interface ResolvedFormSectionModel {
	id: string;
	title: string | null;
	description: string | null;
	rows: Array<ResolvedFormRowModel>;
}

function columnCount(value: unknown): FormLayoutColumnCount {
	return value === 2 || value === 3 ? value : 1;
}

function textOrNull(value: string | null | undefined): string | null {
	return value != null && value.trim() !== '' ? value : null;
}

/**
 * PD-16687 — the sections, rows and cells the renderer draws, with each question's
 * definition attached.
 *
 * - No layout: one untitled section, one question per row in field order (the page as it
 *   was before layouts).
 * - Questions the layout does not place (added after the layout was saved) follow the
 *   last section, one per row, so a question is never silently left off the page.
 * - Forgiving, since a page must draw whatever it is sent: a question that is not on the
 *   form, hidden or already placed is skipped, spans shrink to the row, and a row left
 *   with nothing to show is dropped. The server refuses such layouts on save, so this only
 *   matters for data it never checked.
 *
 * Ids made here (the trailing section and its rows) contain a colon, which a saved
 * layout id cannot, so they never collide with the admin's.
 */
export function resolveLayout(
	definition: Pick<FormDefinitionModel, 'fields' | 'layout'>,
): Array<ResolvedFormSectionModel> {
	const shown = definition.fields.filter(
		(field) => field.fieldType !== 'hidden',
	);
	const byKey = new Map(shown.map((field) => [field.fieldKey, field]));
	const placed = new Set<string>();
	const sections: Array<ResolvedFormSectionModel> = [];

	for (const section of definition.layout?.sections ?? []) {
		const rows: Array<ResolvedFormRowModel> = [];
		for (const row of section.rows) {
			const columns = columnCount(row.columns);
			const cells: Array<ResolvedFormCellModel> = [];
			for (const cell of row.cells) {
				const span = Math.min(
					columnCount(cell.span ?? 1),
					columns,
				) as FormLayoutColumnCount;
				// Read as a string: a page may be sent a kind of cell it does not know.
				const kind: string = cell.kind;
				if (cell.kind === 'field') {
					const field = byKey.get(cell.fieldKey);
					if (field == null || placed.has(field.fieldKey)) {
						continue;
					}
					placed.add(field.fieldKey);
					cells.push({
						kind: 'field',
						field,
						span,
						optionColumns:
							cell.optionColumns != null &&
							FORM_LAYOUT_CHOICE_TYPES.includes(field.fieldType)
								? columnCount(cell.optionColumns)
								: null,
					});
				} else if (kind === 'text' && textOrNull(cell.text) != null) {
					cells.push({
						kind: 'text',
						id: cell.id,
						style: cell.style === 'heading' ? 'heading' : 'paragraph',
						text: cell.text,
						span,
					});
				}
			}
			if (cells.length > 0) {
				rows.push({ id: row.id, columns, cells });
			}
		}

		const title = textOrNull(section.title);
		const description = textOrNull(section.description);
		if (rows.length > 0 || title != null || description != null) {
			sections.push({ id: section.id, title, description, rows });
		}
	}

	const rest = shown.filter((field) => !placed.has(field.fieldKey));
	if (rest.length > 0) {
		sections.push({
			id: ':unplaced',
			title: null,
			description: null,
			rows: rest.map((field) => ({
				id: `:field:${field.fieldKey}`,
				columns: 1,
				cells: [{ kind: 'field', field, span: 1, optionColumns: null }],
			})),
		});
	}
	return sections;
}

/** The questions in the order the page shows them; hidden values last, in field order. */
export function fieldsInPageOrder(
	definition: Pick<FormDefinitionModel, 'fields' | 'layout'>,
): Array<FormFieldDefinitionModel> {
	const shown = resolveLayout(definition).flatMap((section) =>
		section.rows.flatMap((row) =>
			row.cells.flatMap((cell) => (cell.kind === 'field' ? [cell.field] : [])),
		),
	);
	return [
		...shown,
		...definition.fields.filter((field) => field.fieldType === 'hidden'),
	];
}

/** The plain message for a form-level code; an unknown code gets a general one. */
export function formErrorMessage(code: string): string {
	const messages: Readonly<Record<string, string>> = FORM_ERROR_MESSAGES;
	return Object.hasOwn(messages, code)
		? (messages[code] ?? '')
		: 'Something went wrong. Please try again.';
}

/** The effective maximum length the input should allow for a field. */
export function inputMaxLength(
	field: Pick<FormFieldDefinitionModel, 'fieldType' | 'validationRules'>,
	ceilings: Readonly<Record<string, number>>,
): number | undefined {
	const ceiling = ceilings[field.fieldType];
	const adminMax = field.validationRules?.maxLength;
	if (ceiling == null) {
		return adminMax;
	}
	return adminMax != null && adminMax < ceiling ? adminMax : ceiling;
}
